import React, { useMemo, useCallback, useRef } from "react";
import type { MouseEventHandler } from "react";

import cn from "classnames";

import { mapButtonCircleViewToLoaderView } from "./constants/views";
import { boxForwardRef } from "../../../helpers/common/Box/Box";
import { ButtonCore } from "../../../helpers/common/ButtonCore";
import { useMouseDownAnimation } from "../../../hooks/useMouseDownAnimation";
import {
    buttonCircleSizeToAssetSizeDict,
    buttonCircleSizeToCounterSizeDict,
} from "../../../models/sizes";
import { getChildrenRecord } from "../../../utils/react";
import { getLoaderProps } from "../../../utils/ui/getLoaderProps";
import { UniBoxAdditional } from "../../UniBox";

import type { ButtonCircleProps, ButtonCircleState } from "./types";
import type { BoxComponentWithDefault } from "../../../helpers/common/Box/Box";

import classes from "./ButtonCircle.styl";

export const BUTTON_CIRCLE_DISPLAY_NAME = "ButtonCircle";
const DISABLED_STATES: ButtonCircleState[] = ["disabled", "loading"];
export const BUTTON_LABEL_ERROR =
    "В ButtonCircle не был передан ни текст, ни aria-label";

/**
 * Базовый компонент, который сообщает пользователю о возможности совершения действия.
 *
 * Используется для быстрых действий, смысл которых понятен через метафору иконки, а форма кнопки должна иметь форму круга для поддержания композиции. В качестве основного контента передаётся только Icon
 *
 * В качестве `as` ожидаем, что будет передан 'button', 'a' или компонент, реализующий функцию навигации (например, `RoutingLink` на большом вебе).
 * Более подробную информацию можно найти [тут](/docs/документация-разработчикам-дс-инструкция-по-разработке-полиморфных-компонентов--docs).
 *
 * <a href="https://www.figma.com/design/LYZAXE33pUg2cSL5mmUHYq/%F0%9F%8D%91-Buttons-%26-Tabs-%26-Chips?node-id=1309-20360&node-type=frame">Спека</a>
 * @constructor
 */

export const ButtonCircle = boxForwardRef<
    BoxComponentWithDefault<"button", ButtonCircleProps>
>(
    (
        {
            as = "button",
            customColorsStyles,
            view = "primary",
            size = "56",
            state = "enabled",
            stroke: withStroke = false,
            onMouseDown,
            "aria-label": ariaLabel,
            "data-tsid": testId,
            children,
            customVarsStyles,
            ...props
        },
        ref,
    ) => {
        const isDisabled = DISABLED_STATES.includes(state);
        const isLoading = state === "loading";

        const assetSize = buttonCircleSizeToAssetSizeDict[size!];
        const counterSize = buttonCircleSizeToCounterSizeDict[size!];
        const assetView = mapButtonCircleViewToLoaderView[view];

        const { Counter, Icon, Label, Loader } = useMemo(
            () =>
                getChildrenRecord(children, BUTTON_CIRCLE_DISPLAY_NAME, {
                    Counter: {
                        props: {
                            size: counterSize,
                        },
                        optionalRequired: !children,
                        skipElement: size === "16",
                    },
                    Icon: {
                        props: {
                            size: assetSize,
                            containerSize: size,
                            className: classes.icon,
                        },
                        optionalRequired: !children,
                    },
                    Label: {
                        optionalRequired: !children,
                    },
                    Loader: {
                        props: getLoaderProps({
                            size: assetSize,
                            view: assetView,
                            customColorsStyles:
                                classes["loader-custom-color-styles"],
                        }),
                        required: isLoading,
                    },
                }),
            [assetSize, assetView, children, counterSize, isLoading, size],
        );

        const contentElRef = useRef<HTMLDivElement>(null);

        if (!Label && !ariaLabel) {
            throw new Error(BUTTON_LABEL_ERROR);
        }
        const withLabel = size === "56" && Boolean(Label?.props?.children);

        const { clicked, mousedownHandler: mouseDownAnimationHandler } =
            useMouseDownAnimation();

        const mouseDownHandler: MouseEventHandler<HTMLButtonElement> =
            useCallback(
                (ev) => {
                    mouseDownAnimationHandler();
                    onMouseDown?.(ev);
                },
                [mouseDownAnimationHandler, onMouseDown],
            );

        const containerClasses = cn(
            classes.container,
            classes[`__size-${size}`],
            classes[`__view-${view}`],
            {
                [classes["__state-disabled"]]: isDisabled,
            },
            {
                [classes["__state-loading"]]: isLoading,
            },
            {
                [classes["__state-pressed"]]: clicked,
            },
            {
                [classes["__with-label"]]: withLabel,
            },
            {
                [classes["__with-stroke"]]: withStroke,
            },
            customColorsStyles,
            customVarsStyles,
        );

        return (
            <ButtonCore
                as={as}
                {...props}
                wrapperClassName={containerClasses}
                overlaysClassName={classes["overlays-container"]}
                overlaysContainer={contentElRef}
                disabled={isDisabled}
                onMouseDown={mouseDownHandler}
                data-tsid={testId}
                data-uikit={BUTTON_CIRCLE_DISPLAY_NAME}
                aria-label={ariaLabel}
                ref={ref}
            >
                <div className={classes.content} ref={contentElRef}>
                    {isLoading ? Loader : Icon}
                    {Counter && (
                        <UniBoxAdditional>
                            <UniBoxAdditional.SlotTopRight
                                customVarsStyles={classes.counter}
                            >
                                {Counter}
                            </UniBoxAdditional.SlotTopRight>
                        </UniBoxAdditional>
                    )}
                </div>
                <div className={classes.outline} />
                {withLabel && Label}
            </ButtonCore>
        );
    },
);

ButtonCircle.displayName = BUTTON_CIRCLE_DISPLAY_NAME;

export default ButtonCircle;
